import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Slider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  ExpandMore as ExpandMoreIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  People as PeopleIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import Header from "../Header";
import {
  GAME_SOUND_DEFINITIONS,
  GAME_LABELS,
} from "../../../constants/gameSoundDefinitions";
import { playSound, resolveAudioUrl } from "../../../utils/gameAudio";
import useGameSoundChannelMute from "../../../hooks/useGameSoundChannelMute";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const buildEmptyState = () => {
  const state = {};
  Object.entries(GAME_SOUND_DEFINITIONS).forEach(([gameKey, actions]) => {
    state[gameKey] = {};
    actions.forEach((action) => {
      state[gameKey][action.action_key] = {
        game_key: gameKey,
        action_key: action.action_key,
        audio_url: "",
        volume: action.default_volume ?? 1,
        loop: !!action.loop,
        enabled: true,
      };
    });
  });
  return state;
};

const SoundSystemDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [soundState, setSoundState] = useState(buildEmptyState);
  const [uploadingKey, setUploadingKey] = useState(null);
  const previewRef = useRef(null);
  const {
    userMuted,
    adminMuted,
    saving: muteSaving,
    error: muteError,
    setUserMuted,
    setAdminMuted,
  } = useGameSoundChannelMute();

  const getAuthToken = () => localStorage.getItem("auth_token");

  const stopPreview = () => {
    if (previewRef.current) {
      try {
        previewRef.current.pause();
        previewRef.current.currentTime = 0;
      } catch {
        /* ignore */
      }
      previewRef.current = null;
    }
  };

  const fetchSounds = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/game-sounds/admin/all`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || "Failed to load sound configs");
        return;
      }

      const next = buildEmptyState();
      (data.sounds || []).forEach((row) => {
        if (!next[row.game_key]?.[row.action_key]) return;
        next[row.game_key][row.action_key] = {
          game_key: row.game_key,
          action_key: row.action_key,
          audio_url: row.audio_url || "",
          volume: row.volume ?? 1,
          loop: !!row.loop,
          enabled: row.enabled !== 0 && row.enabled !== false,
        };
      });
      setSoundState(next);
    } catch (err) {
      console.error("fetchSounds error", err);
      setError("Failed to load sound configs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSounds();
    return () => stopPreview();
  }, []);

  const updateField = (gameKey, actionKey, field, value) => {
    setSoundState((prev) => ({
      ...prev,
      [gameKey]: {
        ...prev[gameKey],
        [actionKey]: {
          ...prev[gameKey][actionKey],
          [field]: value,
        },
      },
    }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleUpload = async (gameKey, actionKey, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const compositeKey = `${gameKey}:${actionKey}`;
    try {
      setUploadingKey(compositeKey);
      setSaveError(null);

      const formData = new FormData();
      formData.append("audio", file);

      const res = await fetch(`${API_BASE}/api/upload/game-audio`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setSaveError(data.error || "Failed to upload audio");
        return;
      }
      if (data.audioUrl) {
        updateField(gameKey, actionKey, "audio_url", data.audioUrl);
      }
    } catch (err) {
      console.error("audio upload error", err);
      setSaveError("Failed to upload audio");
    } finally {
      setUploadingKey(null);
      if (event.target) event.target.value = "";
    }
  };

  const handlePreview = (gameKey, actionKey) => {
    const cfg = soundState[gameKey]?.[actionKey];
    if (!cfg?.audio_url) return;
    stopPreview();
    previewRef.current = playSound(cfg.audio_url, {
      volume: cfg.volume ?? 1,
      loop: !!cfg.loop,
      ignoreMute: true,
    });
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      setSaveError(null);
      setSaveSuccess(false);
      stopPreview();

      const payload = [];
      Object.values(soundState).forEach((gameSounds) => {
        Object.values(gameSounds).forEach((item) => payload.push(item));
      });

      const res = await fetch(`${API_BASE}/api/game-sounds/admin/all`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ sounds: payload }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setSaveError(data.error || "Failed to save");
        return;
      }
      setSaveSuccess(true);
    } catch (err) {
      console.error("save sounds error", err);
      setSaveError("Failed to save");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#121210" }}>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <VolumeUpIcon sx={{ fontSize: 36, color: "#1DB954" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#fff" }}>
              Sound System
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
              Central audio for Spin the Wheel, Beer Millionaire, and Higher or Lower
              (including Audio Clue notes). Upload a file or paste a URL for each named
              sound. Accepted uploads: MP3, WAV, OGG, M4A, WebM, AAC, MPEG. Buzzer sounds
              are managed on the Buzzer dashboard.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSounds}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saveLoading || loading}
          >
            Save all
          </Button>
        </Box>

        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "#1E1E1E",
            border: "1px solid #333",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#fff" }}>
              Side volume
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
              Turn a side off to silence game sounds on every device of that type
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant={userMuted ? "outlined" : "contained"}
              color={userMuted ? "inherit" : "success"}
              disabled={muteSaving}
              startIcon={userMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              onClick={() => setUserMuted(!userMuted)}
              sx={{
                minWidth: 200,
                ...(userMuted
                  ? { color: "rgba(255,255,255,0.7)", borderColor: "#555" }
                  : {}),
              }}
            >
              {userMuted ? "User Side Off" : "Mute User Side"}
              <PeopleIcon sx={{ ml: 1, fontSize: 18, opacity: 0.85 }} />
            </Button>
            <Button
              variant={adminMuted ? "outlined" : "contained"}
              color={adminMuted ? "inherit" : "info"}
              disabled={muteSaving}
              startIcon={adminMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              onClick={() => setAdminMuted(!adminMuted)}
              sx={{
                minWidth: 200,
                ...(adminMuted
                  ? { color: "rgba(255,255,255,0.7)", borderColor: "#555" }
                  : {}),
              }}
            >
              {adminMuted ? "Admin Side Off" : "Mute Admin Side"}
              <PersonIcon sx={{ ml: 1, fontSize: 18, opacity: 0.85 }} />
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {muteError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {muteError}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Sound settings saved.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          Object.entries(GAME_SOUND_DEFINITIONS).map(([gameKey, actions]) => (
            <Accordion
              key={gameKey}
              defaultExpanded
              sx={{
                mb: 2,
                bgcolor: "#1E1E1E",
                color: "#fff",
                "&:before": { display: "none" },
                borderRadius: "12px !important",
                border: "1px solid #333",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {GAME_LABELS[gameKey] || gameKey}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {actions.map((action) => {
                    const cfg = soundState[gameKey]?.[action.action_key] || {};
                    const compositeKey = `${gameKey}:${action.action_key}`;
                    const previewUrl = resolveAudioUrl(cfg.audio_url);

                    return (
                      <Grid item xs={12} key={action.action_key}>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: "#252525",
                            border: "1px solid #333",
                            borderRadius: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 1.5,
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontWeight: 600 }}>{action.label}</Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={cfg.enabled !== false}
                                  onChange={(e) =>
                                    updateField(gameKey, action.action_key, "enabled", e.target.checked)
                                  }
                                  size="small"
                                />
                              }
                              label="Enabled"
                              sx={{ mr: 0 }}
                            />
                          </Box>

                          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Audio URL"
                              value={cfg.audio_url || ""}
                              onChange={(e) =>
                                updateField(gameKey, action.action_key, "audio_url", e.target.value)
                              }
                              placeholder="https://... or /uploads/sounds/..."
                            />
                            <Button
                              variant="outlined"
                              component="label"
                              disabled={uploadingKey === compositeKey}
                              sx={{ whiteSpace: "nowrap", minWidth: 100 }}
                            >
                              {uploadingKey === compositeKey ? "Uploading..." : "Upload"}
                              <input
                                type="file"
                                accept="audio/*,.mp3,.wav,.ogg,.m4a,.webm,.aac,.mpeg,.mpg,.mpga,.mp2,audio/mpeg,video/mpeg"
                                hidden
                                onChange={(e) => handleUpload(gameKey, action.action_key, e)}
                              />
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => handlePreview(gameKey, action.action_key)}
                              disabled={!previewUrl}
                              sx={{ minWidth: 48 }}
                            >
                              <PlayIcon />
                            </Button>
                            <Button variant="outlined" onClick={stopPreview} sx={{ minWidth: 48 }}>
                              <StopIcon />
                            </Button>
                          </Box>

                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", mb: 0.5, display: "block" }}>
                            Volume
                          </Typography>
                          <Slider
                            value={cfg.volume ?? 1}
                            min={0}
                            max={1}
                            step={0.05}
                            onChange={(_, v) =>
                              updateField(gameKey, action.action_key, "volume", v)
                            }
                            sx={{ color: "#1DB954", maxWidth: 280 }}
                          />
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Container>
    </Box>
  );
};

export default SoundSystemDashboard;
